/**
 * Lightweight API client — replaces @supabase/supabase-js (APE-226).
 *
 * Provides a `supabase`-compatible surface for the parts of the client
 * actually used in this codebase:
 *   - supabase.functions.invoke(name, { body })
 *   - supabase.auth.onAuthStateChange(callback)
 *   - supabase.auth.signOut({ scope })
 *
 * Auth state is tracked via a signed JWT (HS256) stored in localStorage.
 * The JWT is issued by the Worker after successful OTP verification.
 */

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

function getApiBase(): string {
  const url =
    (import.meta as any)?.env?.VITE_API_BASE_URL ??
    (typeof process !== 'undefined'
      ? (process.env as Record<string, string | undefined>).VITE_API_BASE_URL
      : undefined);
  if (!url) throw new Error('VITE_API_BASE_URL is not set');
  return url.replace(/\/$/, '');
}

function getAppId(): string | undefined {
  return (import.meta as any)?.env?.VITE_APP_ID;
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'ia_access_token';

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* Storage unavailable — ignore */
  }
  emitAuthChange(token !== null ? 'SIGNED_IN' : 'SIGNED_OUT', token);
}

// ---------------------------------------------------------------------------
// Auth state listeners
// ---------------------------------------------------------------------------

type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT';
type AuthCallback = (event: AuthEvent, token: string | null) => void;

const listeners = new Set<AuthCallback>();

function emitAuthChange(event: AuthEvent, token: string | null) {
  for (const cb of listeners) {
    try {
      cb(event, token);
    } catch (err) {
      console.error('[apiClient] auth listener error', err);
    }
  }
}

// ---------------------------------------------------------------------------
// functions.invoke
// ---------------------------------------------------------------------------

interface InvokeOptions {
  body?: unknown;
  headers?: Record<string, string>;
}

interface InvokeResult<T = unknown> {
  data: T | null;
  error: { message: string } | null;
}

interface InvokeStreamResult {
  stream: ReadableStream<Uint8Array> | null;
  error: { message: string } | null;
}

function buildRequestHeaders(options: InvokeOptions): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };
  const appId = getAppId();
  if (appId) headers['x-app-id'] = appId;
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function invoke<T = unknown>(
  functionName: string,
  options: InvokeOptions = {},
): Promise<InvokeResult<T>> {
  let apiBase: string;
  try {
    apiBase = getApiBase();
  } catch {
    return { data: null, error: { message: 'VITE_API_BASE_URL not set' } };
  }

  try {
    const resp = await fetch(`${apiBase}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: buildRequestHeaders(options),
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const body = await resp.json() as { error?: string };
        if (body.error) msg = body.error;
      } catch {}
      return { data: null, error: { message: msg } };
    }

    const data = (await resp.json()) as T;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: { message: String(err) } };
  }
}

/**
 * Like `invoke` but returns the raw response body as a ReadableStream.
 * Use for streaming endpoints (e.g. ai_chat_message returns text/event-stream).
 */
async function invokeStream(
  functionName: string,
  options: InvokeOptions = {},
): Promise<InvokeStreamResult> {
  let apiBase: string;
  try {
    apiBase = getApiBase();
  } catch {
    return { stream: null, error: { message: 'VITE_API_BASE_URL not set' } };
  }

  try {
    const resp = await fetch(`${apiBase}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: buildRequestHeaders(options),
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const body = await resp.json() as { error?: string };
        if (body.error) msg = body.error;
      } catch {}
      return { stream: null, error: { message: msg } };
    }

    return { stream: resp.body, error: null };
  } catch (err) {
    return { stream: null, error: { message: String(err) } };
  }
}

// ---------------------------------------------------------------------------
// auth.signOut
// ---------------------------------------------------------------------------

function signOut(_opts?: { scope?: string }): Promise<void> {
  setAccessToken(null);
  return Promise.resolve();
}

// ---------------------------------------------------------------------------
// auth.onAuthStateChange
// ---------------------------------------------------------------------------

function onAuthStateChange(callback: AuthCallback): { data: { subscription: { unsubscribe: () => void } } } {
  listeners.add(callback);
  return {
    data: {
      subscription: {
        unsubscribe() {
          listeners.delete(callback);
        },
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Exported client (supabase-shaped)
// ---------------------------------------------------------------------------

export const apiClient = {
  functions: { invoke, invokeStream },
  auth: { onAuthStateChange, signOut },
} as const;
